import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, LinearProgress, IconButton, Chip, Stack, Collapse, Tooltip } from '@mui/material';
import {
    Close as CloseIcon,
    Email as EmailIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Pause as PauseIcon,
    Stop as StopIcon,
} from '@mui/icons-material';
import { useEmailJobs } from '../contexts/EmailJobContext';
import { useQuery, useMutation, gql } from '@apollo/client';

const GET_BULK_EMAIL_PROGRESS = gql`
    query GetBulkEmailProgress($jobId: String!) {
        getBulkEmailProgress(jobId: $jobId) {
            total
            sent
            failed
            currentId
            status
            message
        }
    }
`;

const PAUSE_BULK_EMAIL = gql`
    mutation PauseBulkEmail($jobId: String!) {
        pauseBulkEmail(jobId: $jobId)
    }
`;

const STOP_BULK_EMAIL = gql`
    mutation StopBulkEmail($jobId: String!) {
        stopBulkEmail(jobId: $jobId)
    }
`;

const EmailJobIndicator = ({ job }) => {
    const { removeJob } = useEmailJobs();
    const [expanded, setExpanded] = useState(true);
    const [pauseBulkEmail] = useMutation(PAUSE_BULK_EMAIL);
    const [stopBulkEmail] = useMutation(STOP_BULK_EMAIL);

    // Continuously poll for progress
    const { data, startPolling, stopPolling } = useQuery(GET_BULK_EMAIL_PROGRESS, {
        variables: { jobId: job.jobId },
        pollInterval: 1000,
        fetchPolicy: 'network-only',
        skip: false, // Always poll when job exists
    });

    const progress = data?.getBulkEmailProgress;

    useEffect(() => {
        if (progress?.status === 'completed' || progress?.status === 'error' || progress?.status === 'stopped') {
            // Stop polling when job is done
            stopPolling();
            // Auto-remove after 5 seconds
            const timer = setTimeout(() => {
                removeJob(job.jobId);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [progress?.status, job.jobId, removeJob, stopPolling]);

    const handlePause = async () => {
        try {
            await pauseBulkEmail({ variables: { jobId: job.jobId } });
        } catch (error) {
            console.error('Failed to pause email job:', error);
        }
    };

    const handleStop = async () => {
        try {
            await stopBulkEmail({ variables: { jobId: job.jobId } });
            stopPolling();
        } catch (error) {
            console.error('Failed to stop email job:', error);
        }
    };

    const handleClose = () => {
        if (progress?.status === 'completed' || progress?.status === 'error' || progress?.status === 'stopped') {
            removeJob(job.jobId);
        } else {
            setExpanded(false);
        }
    };

    const getStatusColor = () => {
        if (!progress) return 'default';
        switch (progress.status) {
            case 'completed':
                return 'success';
            case 'error':
            case 'stopped':
                return 'error';
            case 'paused':
                return 'warning';
            case 'sending':
                return 'primary';
            default:
                return 'default';
        }
    };

    const getStatusIcon = () => {
        if (!progress) return <EmailIcon />;
        switch (progress.status) {
            case 'completed':
                return <CheckCircleIcon />;
            case 'error':
            case 'stopped':
                return <ErrorIcon />;
            case 'paused':
                return <PauseIcon />;
            default:
                return <EmailIcon />;
        }
    };

    const progressPercent =
        progress && progress.total > 0 ? ((progress.sent + progress.failed) / progress.total) * 100 : 0;

    return (
        <Paper
            elevation={6}
            sx={{
                mb: 1,
                minWidth: 320,
                maxWidth: 400,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Box
                sx={{
                    p: 1.5,
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                    '&:hover': {
                        bgcolor: 'action.hover',
                    },
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
                    <Stack direction='row' spacing={1} alignItems='center' sx={{ flex: 1 }}>
                        {getStatusIcon()}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant='subtitle2' noWrap>
                                {job.role || 'Email Sending'}
                            </Typography>
                            {progress && (
                                <Typography variant='caption' color='text.secondary'>
                                    {progress.sent + progress.failed} / {progress.total}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                    <Stack direction='row' spacing={0.5} alignItems='center'>
                        <Chip
                            label={progress?.status || 'starting'}
                            size='small'
                            color={getStatusColor()}
                            sx={{ textTransform: 'capitalize' }}
                        />
                        <IconButton
                            size='small'
                            onClick={e => {
                                e.stopPropagation();
                                handleClose();
                            }}
                        >
                            <CloseIcon fontSize='small' />
                        </IconButton>
                    </Stack>
                </Stack>
            </Box>

            <Collapse in={expanded}>
                <Box sx={{ p: 1.5, pt: 0 }}>
                    {progress && (
                        <>
                            <LinearProgress
                                variant='determinate'
                                value={progressPercent}
                                sx={{ mb: 1, height: 6, borderRadius: 1 }}
                                color={
                                    progress.status === 'error' || progress.status === 'stopped'
                                        ? 'error'
                                        : progress.status === 'completed'
                                        ? 'success'
                                        : 'primary'
                                }
                            />
                            <Stack direction='row' spacing={2} justifyContent='space-between' sx={{ mb: 1 }}>
                                <Typography variant='caption' color='text.secondary'>
                                    Sent: <strong>{progress.sent}</strong>
                                </Typography>
                                <Typography variant='caption' color='text.secondary'>
                                    Failed: <strong>{progress.failed}</strong>
                                </Typography>
                                <Typography variant='caption' color='text.secondary'>
                                    Total: <strong>{progress.total}</strong>
                                </Typography>
                            </Stack>
                            {progress.currentId && (
                                <Typography variant='caption' color='text.secondary' noWrap>
                                    Current: {progress.currentId}
                                </Typography>
                            )}
                            {progress.message && (
                                <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>
                                    {progress.message}
                                </Typography>
                            )}
                        </>
                    )}

                    {progress?.status === 'sending' && (
                        <Stack direction='row' spacing={1} sx={{ mt: 1 }}>
                            <Tooltip title='Pause'>
                                <IconButton size='small' onClick={handlePause}>
                                    <PauseIcon fontSize='small' />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title='Stop'>
                                <IconButton size='small' onClick={handleStop} color='error'>
                                    <StopIcon fontSize='small' />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
};

export default function EmailJobFloatingIndicator() {
    const { activeJobs } = useEmailJobs();

    if (activeJobs.length === 0) return null;

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 80,
                right: 16,
                zIndex: 9999,
                maxHeight: 'calc(100vh - 100px)',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderRadius: '3px',
                },
            }}
        >
            {activeJobs.map(job => (
                <EmailJobIndicator key={job.jobId} job={job} />
            ))}
        </Box>
    );
}
